import { useEffect, useState } from 'react'
import {
  Box,
  Flex,
  Text,
  Button,
  IconButton,
  HStack,
  VStack,
  Divider,
  FormControl,
  FormLabel,
  FormHelperText,
  Switch,
  useColorModeValue,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import {
  clearFunctionalPreferences,
  PRIVACY_PREFERENCES_BAR_EVENT,
  usePrivacyPreferences,
} from '@/utils/privacyPreferences'

const PrivacyPreferencesBar = () => {
  const { t } = useTranslation()
  const { preferences, setPrivacyPreferences } = usePrivacyPreferences()
  const [expanded, setExpanded] = useState(false)
  const [forceOpen, setForceOpen] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [functionalEnabled, setFunctionalEnabled] = useState(preferences.functional)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(preferences.analytics)
  const [marketingEnabled, setMarketingEnabled] = useState(preferences.marketing)
  const bg = useColorModeValue('whiteAlpha.900', 'gray.900')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const titleColor = useColorModeValue('gray.700', 'gray.100')
  const descriptionColor = useColorModeValue('gray.500', 'gray.400')

  useEffect(() => {
    if (!expanded) {
      return
    }
    if (!preferences.timestamp) {
      setFunctionalEnabled(true)
      setAnalyticsEnabled(true)
      setMarketingEnabled(true)
      return
    }
    setFunctionalEnabled(preferences.functional)
    setAnalyticsEnabled(preferences.analytics)
    setMarketingEnabled(preferences.marketing)
  }, [expanded, preferences])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const handleOpen = () => {
      setDismissed(false)
      setForceOpen(true)
      setExpanded(true)
    }

    window.addEventListener(PRIVACY_PREFERENCES_BAR_EVENT, handleOpen)
    return () => {
      window.removeEventListener(PRIVACY_PREFERENCES_BAR_EVENT, handleOpen)
    }
  }, [])

  if (typeof window === 'undefined') {
    return null
  }

  const showBar = !dismissed && (forceOpen || !preferences.timestamp)

  const handleOnlyNecessary = () => {
    setPrivacyPreferences({ functional: false, analytics: false, marketing: false })
    clearFunctionalPreferences()
    setForceOpen(false)
    setExpanded(false)
  }

  const handleAcceptAll = () => {
    setPrivacyPreferences({ functional: true, analytics: true, marketing: true })
    setForceOpen(false)
    setExpanded(false)
  }

  const handleSave = () => {
    setPrivacyPreferences({
      functional: functionalEnabled,
      analytics: analyticsEnabled,
      marketing: marketingEnabled,
    })
    if (!functionalEnabled) {
      clearFunctionalPreferences()
    }
    setForceOpen(false)
    setExpanded(false)
  }

  const handleClose = () => {
    setExpanded(false)
    setForceOpen(false)
    setDismissed(true)
  }

  const detailsLabel = expanded
    ? t('privacyPreferences.hideDetails')
    : t('privacyPreferences.showDetails')

  return (
    <Box
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={1000}
      display={showBar ? 'block' : 'none'}
      bg={bg}
      borderTop="1px solid"
      borderColor={borderColor}
      boxShadow="0 -8px 24px rgba(0,0,0,0.08)"
      backdropFilter="saturate(140%) blur(6px)"
      transition="none"
      maxH={expanded ? { base: '80dvh', md: 'none' } : undefined}
      overflowY={expanded ? { base: 'auto', md: 'visible' } : undefined}
    >
      <Flex direction="column" px={{ base: 3, md: 6 }} py={{ base: 2.5, md: 3 }}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
          gap={{ base: 2, md: 3 }}
        >
          <Box>
            <HStack spacing={2} align="center" flexWrap="wrap">
              <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
                {t('privacyPreferences.bannerTitle')}
              </Text>
          <Button
            size="xs"
            variant="link"
            colorScheme="blue"
            transition="none"
            onClick={() => setExpanded(value => !value)}
          >
            {detailsLabel}
          </Button>
        </HStack>
            {!expanded && (
              <Text fontSize="xs" color={descriptionColor} mt={1}>
                {t('privacyPreferences.bannerDescription')}
              </Text>
            )}
          </Box>
          <HStack
            spacing={{ base: 1, sm: 2 }}
            w={{ base: 'full', md: 'auto' }}
            pr={{ base: 16, md: 0 }}
            align="stretch"
            justify="flex-end"
          >
            <Button
              flex={{ base: 1, md: 'initial' }}
              size="sm"
              colorScheme="blue"
              transition="none"
              onClick={handleAcceptAll}
            >
              {t('privacyPreferences.acceptAll')}
            </Button>
            <Button
              flex={{ base: 1, md: 'initial' }}
              size="sm"
              fontSize="xs"
              whiteSpace={{ base: 'normal', md: 'nowrap' }}
              variant="ghost"
              transition="none"
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              onClick={handleOnlyNecessary}
            >
              {t('privacyPreferences.onlyNecessary')}
            </Button>
            <IconButton
              order={{ base: -1, md: 0 }}
              flexShrink={0}
              aria-label={t('common.close', { defaultValue: 'Close' })}
              icon={<Box as="span" fontSize="xs">x</Box>}
              size="xs"
              variant="ghost"
              transition="none"
              _hover={{ bg: 'transparent' }}
              _active={{ bg: 'transparent' }}
              onClick={handleClose}
            />
          </HStack>
        </Flex>
        {expanded && (
          <Box mt={3}>
            <VStack align="stretch" spacing={4}>
              <Text fontSize="sm" color={descriptionColor}>
                {t('privacyPreferences.manageDescription')}
              </Text>
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color={titleColor}>
                  {t('privacyPreferences.necessaryTitle')}
                </Text>
                <Text fontSize="sm" color={descriptionColor} mt={1}>
                  {t('privacyPreferences.necessaryDescription')}
                </Text>
              </Box>
              <Divider />
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={1} fontWeight="semibold">
                    {t('privacyPreferences.functionalLabel')}
                  </FormLabel>
                  <FormHelperText mt={0}>
                    {t('privacyPreferences.functionalDescription')}
                  </FormHelperText>
                </Box>
                <Switch
                  isChecked={functionalEnabled}
                  onChange={event => setFunctionalEnabled(event.target.checked)}
                  sx={{
                    '.chakra-switch__track': { transition: 'none' },
                    '.chakra-switch__thumb': { transition: 'none' },
                  }}
                />
              </FormControl>
              <Divider />
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={1} fontWeight="semibold">
                    {t('privacyPreferences.analyticsLabel')}
                  </FormLabel>
                  <FormHelperText mt={0}>
                    {t('privacyPreferences.analyticsDescription')}
                  </FormHelperText>
                </Box>
                <Switch
                  isChecked={analyticsEnabled}
                  onChange={event => setAnalyticsEnabled(event.target.checked)}
                  sx={{
                    '.chakra-switch__track': { transition: 'none' },
                    '.chakra-switch__thumb': { transition: 'none' },
                  }}
                />
              </FormControl>
              <Divider />
              <FormControl display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <FormLabel mb={1} fontWeight="semibold">
                    {t('privacyPreferences.marketingLabel')}
                  </FormLabel>
                  <FormHelperText mt={0}>
                    {t('privacyPreferences.marketingDescription')}
                  </FormHelperText>
                </Box>
                <Switch
                  isChecked={marketingEnabled}
                  onChange={event => setMarketingEnabled(event.target.checked)}
                  sx={{
                    '.chakra-switch__track': { transition: 'none' },
                    '.chakra-switch__thumb': { transition: 'none' },
                  }}
                />
              </FormControl>
              <HStack justify="flex-end">
                <Button
                  size="sm"
                  colorScheme="blue"
                  transition="none"
                  onClick={handleSave}
                >
                  {t('privacyPreferences.save')}
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}
      </Flex>
    </Box>
  )
}

export default PrivacyPreferencesBar
